import { z } from 'zod';
import { DraftFeedback } from '@/entities/enums/DraftFeedback.ts';
import type { DraftFeedbackReason } from '@/entities/enums/DraftFeedbackReason.ts';

// Wire status mirrors backend App\Data\Ai\Drafting\Enums\DraftResponseStatus.
export const DraftResponseStatus = {
    Completed: 'completed',
    Failed: 'failed',
    Cancelled: 'cancelled',
    Unsupported: 'unsupported',
} as const;

export type DraftResponseStatus = (typeof DraftResponseStatus)[keyof typeof DraftResponseStatus];

export const EmailAiDraftLocaleSchema = z.enum(['en', 'fr']);

export const GenerateEmailDraftRequestSchema = z.object({
    locale: EmailAiDraftLocaleSchema,
    requestId: z.string().uuid(),
    existingSubject: z.string().nullable().optional(),
});

const CompletedEmailDraftResponseDataSchema = z.object({
    interaction_id: z.number(),
    trace_id: z.string().nullable(),
    status: z.literal(DraftResponseStatus.Completed),
    draft: z.object({
        subject: z.string(),
        body: z.string(),
    }),
});

const CancelledEmailDraftResponseDataSchema = z.object({
    interaction_id: z.number(),
    trace_id: z.string().nullable(),
    status: z.literal(DraftResponseStatus.Cancelled),
    draft: z.null(),
});

export const DraftUnsupportedReason = {
    UnsupportedRequest: 'unsupported_request',
    MissingStatusCode: 'missing_status_code',
    UnknownStatusCode: 'unknown_status_code',
    Unsupported: 'unsupported',
} as const;

export type DraftUnsupportedReason =
    (typeof DraftUnsupportedReason)[keyof typeof DraftUnsupportedReason];

const DraftUnsupportedReasonSchema = z.enum([
    DraftUnsupportedReason.UnsupportedRequest,
    DraftUnsupportedReason.MissingStatusCode,
    DraftUnsupportedReason.UnknownStatusCode,
    DraftUnsupportedReason.Unsupported,
]);

const UnsupportedEmailDraftResponseDataSchema = z.object({
    interaction_id: z.number(),
    trace_id: z.string().nullable(),
    status: z.literal(DraftResponseStatus.Unsupported),
    draft: z.union([z.null(), z.record(z.string(), z.unknown())]),
    reason: DraftUnsupportedReasonSchema,
});

const GenerateEmailDraftResponseDataSchema = z.discriminatedUnion('status', [
    CompletedEmailDraftResponseDataSchema,
    CancelledEmailDraftResponseDataSchema,
]);

const UpdateEmailDraftResponseDataSchema = z.discriminatedUnion('status', [
    CompletedEmailDraftResponseDataSchema,
    CancelledEmailDraftResponseDataSchema,
    UnsupportedEmailDraftResponseDataSchema,
]);

export const GenerateEmailDraftResponseSchema = z.object({
    data: GenerateEmailDraftResponseDataSchema,
});

export const UpdateEmailDraftRequestSchema = z.object({
    locale: EmailAiDraftLocaleSchema,
    requestId: z.string().uuid(),
    subject: z.string(),
    body: z.string(),
    instructions: z.string().nullable().optional(),
});

export const UpdateEmailDraftResponseSchema = z.object({
    data: UpdateEmailDraftResponseDataSchema,
});

export const CancelDraftRequestSchema = z.object({
    requestId: z.string().uuid(),
    channel: z.enum(['email', 'sms']),
});

const CancelDraftStatusSchema = z.enum([
    DraftResponseStatus.Completed,
    DraftResponseStatus.Failed,
    DraftResponseStatus.Cancelled,
]);

export const CancelDraftResponseSchema = z.object({
    data: z.object({
        status: CancelDraftStatusSchema,
    }),
});

export const AiDraftFailureReason = {
    NotEntitled: 'not_entitled',
    RateLimited: 'rate_limited',
    ServiceLead: 'service_lead',
    Cancelled: 'cancelled',
    Unsupported: 'unsupported',
} as const;

export type AiDraftFailureReason = (typeof AiDraftFailureReason)[keyof typeof AiDraftFailureReason];

export const AiDraftErrorCode = {
    NotEntitled: 'ai_draft_not_entitled',
    RateLimited: 'ai_draft_rate_limited',
    ServiceLeadNotSupported: 'ai_draft_service_lead_not_supported',
} as const;

export type AiDraftErrorCode = (typeof AiDraftErrorCode)[keyof typeof AiDraftErrorCode];

export type AiDraftRateLimitFailureResult = {
    ok: false;
    reason: typeof AiDraftFailureReason.RateLimited;
    rateLimitSeconds: number;
};

export type GenerateEmailDraftRequest = z.infer<typeof GenerateEmailDraftRequestSchema>;
export type GenerateEmailDraftResponse = z.infer<typeof GenerateEmailDraftResponseSchema>;

export interface CompletedEmailAiDraftResult {
    subject: string;
    body: string;
    interactionId: number;
    traceId: string | null;
}

export interface CancelledEmailAiDraftResult {
    status: 'cancelled';
    interactionId: number;
    traceId: string | null;
}

export interface UnsupportedEmailAiDraftResult {
    status: 'unsupported';
    interactionId: number;
    traceId: string | null;
    reason: DraftUnsupportedReason;
}

export type GenerateEmailAiDraftResult = CompletedEmailAiDraftResult | CancelledEmailAiDraftResult;

export type UpdateEmailAiDraftResult = GenerateEmailAiDraftResult | UnsupportedEmailAiDraftResult;

export type UpdateEmailDraftRequest = z.infer<typeof UpdateEmailDraftRequestSchema>;
export type UpdateEmailDraftResponse = z.infer<typeof UpdateEmailDraftResponseSchema>;

export interface SubmitDraftFeedbackPayload {
    interactionId: number;
    feedback: DraftFeedback;
    reasons?: DraftFeedbackReason[];
    details?: string | null;
}

export const SmsAiDraftLocaleSchema = z.enum(['en', 'fr']);

export const GenerateSmsDraftRequestSchema = z.object({
    locale: SmsAiDraftLocaleSchema,
    requestId: z.string().uuid(),
});

const SmsDraftSchema = z.object({
    text: z.string(),
});

const CompletedSmsInteractionSchema = z.object({
    interaction_id: z.number(),
    trace_id: z.string().nullable(),
    channel: z.literal('sms'),
    status: z.literal(DraftResponseStatus.Completed),
    draft: SmsDraftSchema,
});

const CancelledSmsInteractionSchema = z.object({
    interaction_id: z.number(),
    trace_id: z.string().nullable(),
    channel: z.literal('sms'),
    status: z.literal(DraftResponseStatus.Cancelled),
    draft: SmsDraftSchema.nullable(),
});

const UnsupportedSmsInteractionSchema = z.object({
    interaction_id: z.number(),
    trace_id: z.string().nullable(),
    channel: z.literal('sms'),
    status: z.literal(DraftResponseStatus.Unsupported),
    draft: z.union([z.null(), z.record(z.string(), z.unknown())]),
    reason: DraftUnsupportedReasonSchema,
});

export const GenerateSmsDraftResponseSchema = z.object({
    data: z.discriminatedUnion('status', [
        CompletedSmsInteractionSchema,
        CancelledSmsInteractionSchema,
    ]),
});

export const UpdateSmsDraftRequestSchema = GenerateSmsDraftRequestSchema.extend({
    text: z.string(),
    instructions: z.string(),
});

export const UpdateSmsDraftResponseSchema = z.object({
    data: z.discriminatedUnion('status', [
        CompletedSmsInteractionSchema,
        CancelledSmsInteractionSchema,
        UnsupportedSmsInteractionSchema,
    ]),
});

export type GenerateSmsDraftRequest = z.infer<typeof GenerateSmsDraftRequestSchema>;
export type GenerateSmsDraftResponse = z.infer<typeof GenerateSmsDraftResponseSchema>;
export type UpdateSmsDraftRequest = z.infer<typeof UpdateSmsDraftRequestSchema>;
export type UpdateSmsDraftResponse = z.infer<typeof UpdateSmsDraftResponseSchema>;

export interface SmsAiDraftSuccessResult {
    text: string;
    interactionId: number;
    traceId: string | null;
}

export type SmsAiDraftCommonResult =
    | ({ ok: true } & SmsAiDraftSuccessResult)
    | { ok: false; reason: typeof AiDraftFailureReason.NotEntitled }
    | { ok: false; reason: typeof AiDraftFailureReason.ServiceLead }
    | { ok: false; reason: typeof AiDraftFailureReason.Cancelled }
    | AiDraftRateLimitFailureResult;

export type UpdateSmsAiDraftResult =
    | SmsAiDraftCommonResult
    | {
          ok: false;
          reason: typeof AiDraftFailureReason.Unsupported;
          unsupportedReason: DraftUnsupportedReason;
      };

export type CancelDraftRequest = z.infer<typeof CancelDraftRequestSchema>;
export type CancelDraftResponse = z.infer<typeof CancelDraftResponseSchema>;
